import type { ReactElement, ReactNode } from 'react'
import { Checkbox } from 'app/components/ui/checkbox'

/**
 * The column grammar every notification row shares. Below `md` the columns
 * collapse and each checkbox takes its word back, since there is no column left
 * to head.
 */
export function ChannelHeaderRow({ children }: { children: ReactNode }): ReactElement {
  return (
    <div className="hidden border-b border-default pb-2 md:flex md:items-center">
      <span className="flex-1 text-label-xs font-medium uppercase tracking-wide text-subtle">Notification</span>
      {children}
    </div>
  )
}

export function ChannelHeading({ label }: { label: string }): ReactElement {
  return <span className="w-20 text-center text-label-xs font-medium uppercase tracking-wide text-subtle">{label}</span>
}

export function NotificationRow({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: ReactNode
}): ReactElement {
  return (
    <div className="flex flex-col gap-3 border-b border-default py-4 last:border-b-0 md:flex-row md:items-center md:gap-0">
      <div className="min-w-0 flex-1">
        <p className="text-body-sm font-medium text-strong">{title}</p>
        <p className="mt-0.5 text-body-xs text-subtle">{description}</p>
      </div>
      <div className="flex items-center gap-6 md:gap-0">{children}</div>
    </div>
  )
}

/**
 * Uncontrolled with a `name` where the save path reads the form, controlled
 * where it reads React state.
 */
export function ChannelCheckbox({
  label,
  title,
  name,
  defaultChecked,
  checked,
  onCheckedChange,
  disabled,
}: {
  label: string
  title: string
  name?: string
  defaultChecked?: boolean
  checked?: boolean
  onCheckedChange?: (value: boolean) => void
  disabled?: boolean
}): ReactElement {
  return (
    <div className="flex items-center gap-2 md:w-20 md:justify-center">
      <Checkbox
        aria-label={`${label}: ${title}`}
        name={name}
        value="on"
        defaultChecked={defaultChecked}
        checked={checked}
        onCheckedChange={(value) => onCheckedChange?.(value === true)}
        disabled={disabled}
      />
      <span className="text-body-sm text-subtle md:hidden">{label}</span>
    </div>
  )
}
